import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DataTable, ColumnDef } from "../common/DataTable";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { AlertCircle, MessageSquare } from "lucide-react";
import { Posting } from "@/types/staffing/posting";
import { getPostings, createProcess } from "@/services/staffing/Staffing";
import { openStaffingGroupByReferenceId } from "@/utils/staffingUtil";
import { useApiClient } from "@/context/ApiContext";
import ClientStaffingContextMenu from "./contextMenus/ClientStaffingContextMenu";
import { CurrentUserContext } from "@/context/CurrentUserContext";

interface AssessmentPostingsListProps {
    /** Bare login (no domain), as CurrentUserContext stores it. */
    identifier: string;
    region: string | null;
    active?: boolean;
    staffFullName?: string | null;
    staffRole?: string | null;
}

/**
 * Open Assessment postings for the region. Assessment postings never come back
 * from the match procs, so analyst roles get this table on the Openings widget
 * in addition to their matches. Same interaction model as ClientMatchesList:
 * express-interest checkbox, or a chat button once the user is in process.
 */
const AssessmentPostingsList = ({ identifier, region, active = true, staffFullName, staffRole }: AssessmentPostingsListProps) => {
    const apiClient = useApiClient();
    const { originalUser } = useContext(CurrentUserContext);
    const [postings, setPostings] = useState<Posting[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedPostingIds, setSelectedPostingIds] = useState<Set<number>>(new Set());
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<{ title: string; detail: string } | null>(null);

    const staffWithDomain = `positive\\${identifier}`;

    const fetchPostings = useCallback(() => {
        if (!region) return;
        setLoading(true);
        getPostings({
            filter: `Region eq '${region}' and ServiceType eq 'Assessment' and Status eq 'Active' and Reasons eq null`,
            expand: ['client', 'processes'],
            orderby: 'Created desc',
            top: 20,
        }, apiClient)
            .then(res => setPostings(res.data.value ?? []))
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    }, [region, apiClient]);

    useEffect(() => {
        if (!active) return;
        fetchPostings();
    }, [active, fetchPostings]);

    /** The current user's open process on this posting, if any (drives chat vs checkbox). */
    const myOpenProcess = useCallback((row: Posting) =>
        (row.Processes ?? []).find(p =>
            p.Status !== 'Closed' && p.Staff?.toLowerCase() === staffWithDomain.toLowerCase()
        ) ?? null,
    [staffWithDomain]);

    const openChat = useCallback((row: Posting) => {
        const process = myOpenProcess(row);
        if (!process) return;
        openStaffingGroupByReferenceId(`${process.Id}-${identifier}-${row.ClientId}`);
    }, [myOpenProcess, identifier]);

    const expressInterest = async () => {
        if (selectedPostingIds.size === 0) return;
        setSubmitting(true);
        setError(null);
        try {
            const selected = postings.filter(p => selectedPostingIds.has(p.Id));
            let lastReferenceId: string | null = null;
            for (const posting of selected) {
                const res = await createProcess({
                    postingId: posting.Id,
                    staff: staffWithDomain,
                    source: 'Openings',
                    applicant: false,
                    author: originalUser.LoginName,
                    adHocGroup: {
                        clientId: posting.ClientId,
                        clientName: posting.Client?.NameFL ?? '',
                        staffFullName: staffFullName ?? '',
                        staffRole: staffRole ?? '',
                        staffRegion: region ?? '',
                    }
                }, apiClient);
                lastReferenceId = res.data?.referenceId ?? lastReferenceId;
            }
            setSelectedPostingIds(new Set());
            fetchPostings();
            if (lastReferenceId) openStaffingGroupByReferenceId(lastReferenceId);
        } catch (err: any) {
            if (err?.response?.status === 409) {
                const data = err.response.data;
                setError({ title: data?.title ?? 'Conflict', detail: data?.detail ?? 'A duplicate process entry already exists.' });
            } else {
                console.error(err);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const columns = useMemo<ColumnDef<Posting>[]>(() => [
        {
            key: 'select',
            header: '',
            sticky: true,
            render: (row) => (
                <div className="flex w-8 items-center justify-center">
                    {myOpenProcess(row) != null ? (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-primary"
                            aria-label="Open staffing chat"
                            title="Staffing Chat"
                            onClick={() => openChat(row)}
                        >
                            <MessageSquare className="h-4 w-4" />
                        </Button>
                    ) : (
                        <Checkbox
                            checked={selectedPostingIds.has(row.Id)}
                            disabled={submitting}
                            onCheckedChange={(checked) => {
                                setSelectedPostingIds(prev => {
                                    const next = new Set(prev);
                                    checked ? next.add(row.Id) : next.delete(row.Id);
                                    return next;
                                });
                            }}
                        />
                    )}
                </div>
            )
        },
        {
            key: 'Client', header: 'Client', sortable: true, sticky: true,
            sortValue: (row) => row.Client?.NameFL ?? '',
            render: (row) => <ClientStaffingContextMenu clientId={row.ClientId} clientName={row.Client?.NameFL ?? null} isPosting={true} isMatch={true} isWidget={true} />
        },
        { key: 'Client.Age', header: 'Age', sortable: true },
        { key: 'Client.Gender', header: 'Gender', sortable: true },
        { key: 'Client.ClientStatus', header: 'Status', sortable: true },
        { key: 'Client.ParentCaregiver1City', header: 'City', sortable: true },
        { key: 'FundingSource', header: 'Funding Source', sortable: true },
        { key: 'Telehealth', header: 'Telehealth', sortable: true, render: (row) => row.Telehealth ? 'Yes' : 'No' },
        {
            key: 'Created', header: 'Posted', sortable: true,
            render: (row) => row.Created ? new Date(row.Created).toLocaleDateString() : ''
        },
    ], [selectedPostingIds, submitting, myOpenProcess, openChat]);

    return (
        <div className="flex flex-col gap-2">
            {error && (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>{error.title}</AlertTitle>
                    <AlertDescription>{error.detail}</AlertDescription>
                </Alert>
            )}
            <DataTable data={postings} columns={columns} loading={loading} />
            <div className="flex justify-end">
                <Button
                    size="sm"
                    disabled={selectedPostingIds.size === 0 || submitting}
                    onClick={expressInterest}
                >
                    {submitting
                        ? 'Adding'
                        : `Express interest in ${selectedPostingIds.size} Assessment${selectedPostingIds.size !== 1 ? 's' : ''}`}
                </Button>
            </div>
        </div>
    );
};

export default AssessmentPostingsList;
